/**
 * Расчёт HSI по кнопке «Обновить» — вторая ступень цепочки слоя «Траектории по HSI».
 *
 * Слой умел молча пустеть: прогноз вида в кадре инвалидировался ночной докачкой
 * (`is_outdated=true`), сидов не находилось, эндпоинт честно отдавал пустую
 * коллекцию — и на карте не появлялось ни усиков, ни объяснения. Теперь бэкенд
 * называет причину (`meta.reason`), а кнопка по ней решает: если поле устарело
 * или его нет, запускается расчёт HSI со своей полоской, и по его завершении
 * усики перезапрашиваются.
 *
 * Расчёт стоит минут, поэтому запускается ТОЛЬКО по явному действию человека
 * (`armed`) и ровно один раз на него — эффект усиков срабатывает на каждый сдвиг
 * карты, и без замка панорамирование гоняло бы пересчёт, а неудачный расчёт
 * зациклил бы цепочку. Явных действий два: клик по «Обновить» и ВКЛЮЧЕНИЕ самого
 * тумблера «Траектории HSI» — второе добавлено 11.08.2026, до того тумблер,
 * включённый после докачки, не запускал ничего и человек оставался с пустой
 * картой без объяснений.
 *
 * Чинится ВСЕГДА дата якоря, а не ползунка: усики растут из якорного поля
 * (последний прогноз ≤ «сегодня»), ползунок лишь режет их длину.
 */

import type { BBox, HsiDriftReason } from '../api'
import { t } from '../i18n'

export type { HsiDriftReason }

/** Шаг опроса прогресса. Как у основного пайплайна: чаще незачем, реже — рвано. */
export const HSI_POLL_MS = 2000

/** Через сколько сдаёмся. Расчёт поля на вьюпорт идёт минуты; 20 — потолок с запасом. */
export const HSI_POLL_TIMEOUT_MS = 1_200_000

/**
 * Можно ли запускать расчёт прямо сейчас: течения не качаются.
 *
 * Расчёт идёт ПО течениям, поэтому запущенный посреди докачки считал бы по
 * старым. Но «докачка не идёт» — это не только `ready`: когда пересчёт заказан
 * ТУМБЛЕРОМ, а не кнопкой, пайплайн вообще не запускался и стоит в `idle`.
 * Прежняя проверка `state === 'ready'` этого не различала, и цепочка от тумблера
 * не могла бы сработать никогда (регрессия 11.08.2026, Чукотка, сельдь).
 *
 * `failed` — не «можно»: течения могли доехать наполовину, и поле, посчитанное
 * по ним, врало бы молча.
 */
export function currentsSettled(
  state: 'idle' | 'preparing' | 'manifest' | 'queued' | 'downloading' | 'ready' | 'failed',
): boolean {
  return state === 'idle' || state === 'ready'
}

/** Состояние полоски расчёта; null на экране означает «показывать нечего». */
export interface HsiRecomputeProgress {
  percent: number
  done: boolean
  error: string | null
  /** Стадия задачи — код статуса сервера (`processing` | `rendering` | …).
   *
   *  Прогон состоит из двух РАЗНЫХ работ, и подпись обязана их различать: сам
   *  расчёт идёт секунды (замер 14.08.2026: 4,7 с на 5166 узлов мили), а следом
   *  задача встаёт в однопоточную очередь тайлов и ждёт там чужой рендер —
   *  401 секунда на 19 460 тайлов. Одна подпись на весь прогон превращала это
   *  ожидание в «Считаем HSI… 80 %» и заставляла искать несуществующую
   *  медленную арифметику.
   *
   *  Берём КОД, а не серверный `current_step`: тот по-русски, а кабинет
   *  двуязычный — текст с сервера в него не пускают. */
  stage?: string
}

/** Статус задачи, при котором расчёт уже позади и строятся тайлы. */
const RENDERING = 'rendering'

/**
 * Запускать ли расчёт HSI.
 *
 * Пересчёт помогает ровно в двух случаях: поля нет (`no_hsi`) или оно
 * инвалидировано докачкой (`stale_hsi`). Свежее поле из нулей (`empty_field`)
 * — честный ответ «вид тут не живёт», а `no_currents` чинится докачкой течений:
 * гонять на них минуты работы значит обещать пользователю результат, которого
 * не будет.
 *
 * `stale_hsi` приходит и на НЕПУСТОЙ ответ: разъеденный кадр снаружи неотличим
 * от целого — усики есть, просто не везде (регрессия 2026-08-06, обрыв усиков
 * посреди экрана). Дата ползунка на решение не влияет: чиним якорь.
 */
export function shouldRecomputeHsi({
  armed,
  enabled,
  ready,
  reason,
}: {
  armed: boolean
  enabled: boolean
  /** Течения не качаются прямо сейчас (см. `currentsSettled`). Расчёт HSI идёт
   *  ПО течениям: запущенный посреди докачки, он считал бы по старым. */
  ready: boolean
  reason: HsiDriftReason | null
  anchor: string | null
  date: string
}): boolean {
  if (!armed || !enabled || !ready) return false
  return reason === 'no_hsi' || reason === 'stale_hsi'
}

/** Подпись полоски. Молча не исчезает ни при одном исходе — как у полоски судов.
 *
 *  `p.error` может быть и нашим ключом каталога (`errors.*`, `models.hsi.*`), и
 *  сырым текстом от сервера: `t()` на неизвестном ключе возвращает его как есть,
 *  поэтому оба случая печатаются одинаково корректно. */
export function hsiRecomputeLabel(p: HsiRecomputeProgress): string {
  const line = progressLine(p)
  return t(line.key, line.params)
}

/** Строка одного прогресса расчёта — общая для подписи полоски и строки
 *  статуса, чтобы правило подписи жило в одном месте. */
function progressLine(p: HsiRecomputeProgress): HsiStatusLine {
  if (p.error) {
    return {
      key: 'models.hsi.recompute.failed',
      params: { reason: t(p.error) },
      tone: 'error',
      percent: null,
    }
  }
  if (p.done) return { key: 'models.hsi.recompute.done', tone: 'normal', percent: null }
  // Незнакомая (или отсутствующая) стадия — прежняя подпись: полоска без
  // подписи хуже подписи неточной.
  return {
    key: p.stage === RENDERING
      ? 'models.hsi.recompute.rendering'
      : 'models.hsi.recompute.running',
    params: { percent: p.percent },
    tone: 'normal',
    percent: p.percent,
  }
}

/** Строка статуса HSI под кнопкой: ключ каталога плюс подстановки. */
export interface HsiStatusLine {
  key: string
  params?: Record<string, string | number>
  tone: 'normal' | 'warn' | 'error'
  /** Доля полоски, если идёт расчёт; null — полоски нет. */
  percent: number | null
}

/**
 * Что писать под кнопкой про поле HSI.
 *
 * Прогресс расчёта описывает КАДР, в котором его заказали, а ответ ручки усиков
 * — кадр, который на экране СЕЙЧАС. Бой 25.09.2026: пересчитан кадр
 * 42–46° с.ш. / 144–152° в.д., человек отдалился на весь Дальний Восток, поле
 * там было помечено устаревшим, усики вышли клочками — а под кнопкой стояло
 * «HSI рассчитан». Поэтому «рассчитан» говорится только когда текущий ответ это
 * подтверждает, а устаревшее или непосчитанное поле называется словами и без
 * всякого расчёта: прежде `stale_hsi` на экран не выходил вовсе, и клочки
 * усиков были неотличимы от «вид тут не живёт».
 *
 * Пока кнопка качает, подсказки нет: докачка сама метит поле устаревшим, и
 * звать нажать работающую кнопку значило бы соврать о состоянии.
 */
export function hsiStatusLine({
  progress,
  reason,
  busy,
}: {
  progress: HsiRecomputeProgress | null
  reason: HsiDriftReason | null
  busy: boolean
}): HsiStatusLine | null {
  if (progress && (progress.error || !progress.done)) return progressLine(progress)
  if (reason === 'stale_hsi' || reason === 'no_hsi') {
    if (busy) return null
    return {
      key: reason === 'stale_hsi' ? 'models.hsi.stale' : 'models.hsi.missing',
      tone: 'warn',
      percent: null,
    }
  }
  if (progress?.done && reason === 'ok') return progressLine(progress)
  return null
}

export interface HsiRecomputeParams {
  species: number
  /** Дата ползунка — запасной вариант, когда якоря нет (поля не было вовсе). */
  date: string
  /** Дата якоря усиков (`meta.anchor`). Именно её и чиним. */
  anchor: string | null
  depth: number
  bbox: BBox
}

interface RecomputeDeps {
  prepare: (body: {
    bbox: BBox
    date: string
    layers: string[]
    species_id: number
    depth: number
    kind: 'model'
    side_jobs: boolean
  }) => Promise<{ task_id: string }>
  confirm: (taskId: string) => Promise<unknown>
  progress: (taskId: string) => Promise<{
    progress_percent: number
    status: string
    error_message?: string | null
  }>
  onProgress: (p: HsiRecomputeProgress) => void
  sleep?: (ms: number) => Promise<void>
}

const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e))

/**
 * prepare → confirm → опрос прогресса. Тот же рельс, что считает HSI в модели
 * «Прогноз», но со своим состоянием: полоска расчёта живёт рядом с основной и
 * кнопку не блокирует.
 *
 * Модалку объёма пропускаем сознательно: пользователь уже нажал «Обновить» —
 * второй вопрос про докачку он не задавал (правило «карта не спрашивает»).
 */
export async function runHsiRecompute(
  params: HsiRecomputeParams,
  { prepare, confirm, progress, onProgress, sleep = wait }: RecomputeDeps,
): Promise<void> {
  // Стартовый кадр — сразу: первый опрос приходит через секунды, и всё это время
  // экран выглядел бы так, будто клик не сработал.
  onProgress({ percent: 0, done: false, error: null })

  let taskId: string
  try {
    const resp = await prepare({
      bbox: params.bbox,
      // Якорь, а не ползунок: усики растут из якорного поля, и разъеден именно
      // оно. Считая дату ползунка, мы чинили целый день и не трогали больной.
      date: params.anchor ?? params.date,
      layers: ['probability'],
      species_id: params.species,
      depth: params.depth,
      kind: 'model',
      // Суда — работа кнопки, а не расчёта поля. Без этого второй prepare ставил
      // ВТОРОЕ задание добора на ту же область: оно прогоняло тех же судов
      // впустую и стояло в очереди за первым.
      side_jobs: false,
    })
    taskId = resp.task_id
    await confirm(taskId)
  } catch (e) {
    onProgress({ percent: 0, done: true, error: msg(e) })
    return
  }

  const attempts = Math.ceil(HSI_POLL_TIMEOUT_MS / HSI_POLL_MS)
  for (let i = 0; i < attempts; i++) {
    let p: Awaited<ReturnType<RecomputeDeps['progress']>>
    try {
      p = await progress(taskId)
    } catch (e) {
      onProgress({ percent: 0, done: true, error: msg(e) })
      return
    }
    if (p.status === 'completed') {
      onProgress({ percent: 100, done: true, error: null })
      return
    }
    if (p.status === 'failed' || p.status === 'data_unavailable') {
      // «Свежие данные или честное падение»: data_unavailable — не ошибка кода,
      // а отсутствие данных, и называть его надо своими словами.
      onProgress({
        percent: p.progress_percent,
        done: true,
        error:
          p.error_message ||
          (p.status === 'data_unavailable'
            ? 'models.hsi.recompute.dataUnavailable'
            : 'models.hsi.recompute.error'),
      })
      return
    }
    onProgress({ percent: p.progress_percent, done: false, error: null, stage: p.status })
    await sleep(HSI_POLL_MS)
  }
  onProgress({ percent: 0, done: true, error: 'models.hsi.recompute.tooLong' })
}
